import {
  type CustomerTotals,
  type CustomerTotalsRequest,
  useGetCustomerTotalsQuery,
} from "@/services/backend/inspections/stats-service";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  DataTable,
  DataTableContainer,
  useDataTableControllerWithSorting,
} from "@/shared/components/data-table";
import { CustomerName } from "@/shared/components/domain/customer/customer-name";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { Label } from "@/shared/components/ui/label";
import { PaginationLimitOffset } from "@/shared/components/ui/pagination-limit-offset";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";

function firstDayOfLastMonth(): Date {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function lastDayOfLastMonth(): Date {
  const d = new Date();
  d.setDate(0);
  d.setHours(23, 59, 59, 999);
  return d;
}

const tableColumns: ColumnDef<CustomerTotals>[] = [
  {
    id: "customer",
    header: "Kunde",
    cell: ({ row }) => <CustomerName customerId={row.original.customerId} />,
  },
  {
    id: "surfaceMicrobialCount",
    accessorKey: "surfaceMicrobialCount",
    header: "AK",
    cell: ({ row }) => row.original.surfaceMicrobialCount,
  },
  {
    id: "airMicrobialCount",
    accessorKey: "airMicrobialCount",
    header: "LK",
    cell: ({ row }) => row.original.airMicrobialCount,
  },
  {
    id: "differentiationCount",
    accessorKey: "differentiationCount",
    header: "Differenzierungen",
    cell: ({ row }) => row.original.differentiationCount,
  },
];

export function StatsOverviewTable() {
  const [request, setRequest] = useState<CustomerTotalsRequest>({
    from: firstDayOfLastMonth(),
    to: lastDayOfLastMonth(),
    limit: 20,
    offset: 0,
  });

  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useGetCustomerTotalsQuery(request, {
    refetchOnMountOrArgChange: true,
  });

  const { table } = useDataTableControllerWithSorting(
    tableColumns,
    list?.items ?? [],
  );

  if (error) return <RTKQueryErrorAlert error={error} />;

  return (
    <div className="flex w-full flex-col space-y-4">
      <div className="flex items-end gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>Von</Label>
          <DatePicker
            date={request.from}
            onDateChange={(val) =>
              setRequest({ ...request, from: val as Date, offset: 0 })
            }
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Bis</Label>
          <DatePicker
            date={request.to}
            onDateChange={(val) =>
              setRequest({ ...request, to: val as Date, offset: 0 })
            }
          />
        </div>
      </div>

      <DataTableContainer className="relative">
        <DataTable table={table} columns={tableColumns} />
        {(isFetching || isLoading) && (
          <div className="bg-background/60 pointer-events-none absolute inset-0 flex items-center justify-center backdrop-blur-sm">
            <span className="text-sm opacity-70">Laden…</span>
          </div>
        )}
      </DataTableContainer>

      {list != null && list.count > 0 && (
        <div className="flex justify-end">
          <PaginationLimitOffset
            offset={request.offset}
            limit={request.limit}
            totalCount={list.count}
            setLimitOffset={(limit, offset) =>
              setRequest({ ...request, limit, offset })
            }
          />
        </div>
      )}
    </div>
  );
}
